// Hausstil der Emails: eine Schrift, Größen wie im Outlook-Desktop-Editor, Tabellen wie die Email-Bausteine
// (templates/clientSpecific/email_bb/general/tableAddition.twig + td.twig).

import { HOUSE_FONT } from '@clientSpecific/email/editorStyle';

export const FONT_FAMILY = HOUSE_FONT.family;
export const HOUSE_FONT_LABEL = HOUSE_FONT.label;
export const DEFAULT_FONT_SIZE = '10pt';
export const DEFAULT_COLOR = 'black';

// Schriftauswahl: Hausschrift + gängige Mail-Schriften (eingefügte Texte bringen sonst z.B. Aptos/Arial mit)
export const FONT_FAMILIES = [
    {label: 'Arial', value: 'Arial, sans-serif'},
    {label: 'Aptos', value: 'Aptos, sans-serif'},
    {label: 'Calibri', value: 'Calibri, sans-serif'},
    {label: 'Georgia', value: 'Georgia, serif'},
    {label: 'Times New Roman', value: "'Times New Roman', serif"},
    {label: 'Verdana', value: 'Verdana, sans-serif'},
];

// "'Aptos',sans-serif" → "Aptos" (erste Schrift ohne Anführungszeichen)
export function primaryFont(value) {
    return String(value || '').split(',')[0].replace(/["']/g, '').trim();
}

export function isHouseFont(value) {
    return primaryFont(value).toLowerCase() === HOUSE_FONT.label.toLowerCase();
}

export const FONT_SIZES = ['8pt', '9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '20pt', '22pt', '24pt', '26pt', '28pt', '36pt', '48pt', '72pt'];

// Outlook: "Designfarben" gekürzt auf die Travelize-Markenfarben + Standardfarben
export const TEXT_COLORS = ['#000000', '#6A6C6F', '#C00000', '#FF0000', '#E78E7C', '#FFC000', '#D6B874', '#00B050', '#87A873', '#0066CC', '#60A4AB', '#7030A0', '#9588AF'];
export const HIGHLIGHT_COLORS = ['#FFFF00', '#00FF00', '#00FFFF', '#FF00FF', '#D9D9D9', '#FCE4D6', '#E2EFDA', '#DDEBF7'];

export const TABLE_BORDER_COLOR = '#D0CECE';
export const TABLE_HEADER_BACKGROUND = '#E7E6E6';

export const INDENT_STEP_PT = 36;
export const MAX_INDENT = 8;

export const BASE_TEXT_STYLE = `font-family:${FONT_FAMILY};font-size:${DEFAULT_FONT_SIZE};color:${DEFAULT_COLOR}`;

// px/em/Schlüsselwörter → nächste erlaubte pt-Größe; null wenn es die Standardgröße ist
export function normalizeFontSize(value) {
    if (!value) {
        return null;
    }
    const keywords = {'xx-small': 7.5, 'x-small': 7.5, small: 10, medium: 12, large: 13.5, 'x-large': 18, 'xx-large': 24};
    let pt = null;
    const v = String(value).trim().toLowerCase();
    const number = parseFloat(v);
    if (keywords[v]) {
        pt = keywords[v];
    } else if (v.endsWith('pt')) {
        pt = number;
    } else if (v.endsWith('px')) {
        pt = number * 0.75;
    } else if (v.endsWith('em') || v.endsWith('rem')) {
        pt = number * parseFloat(DEFAULT_FONT_SIZE);
    } else if (v.endsWith('%')) {
        pt = number / 100 * parseFloat(DEFAULT_FONT_SIZE);
    }
    if (!pt || isNaN(pt)) {
        return null;
    }
    const nearest = FONT_SIZES.reduce((best, size) =>
        Math.abs(parseFloat(size) - pt) < Math.abs(parseFloat(best) - pt) ? size : best);
    return nearest === DEFAULT_FONT_SIZE ? null : nearest;
}

// Schwarz/Automatisch zählt als "keine Farbe", damit der Text dem Hausstil folgt
export function normalizeColor(value) {
    if (!value) {
        return null;
    }
    const v = String(value).trim().toLowerCase().replace(/\s+/g, '');
    if (['', 'black', '#000', '#000000', 'rgb(0,0,0)', 'windowtext', 'inherit', 'initial', 'currentcolor', 'auto', 'transparent', 'rgba(0,0,0,0)'].includes(v)) {
        return null;
    }
    return toHex(v) || null;
}

export function normalizeBackground(value) {
    if (!value) {
        return null;
    }
    const v = String(value).trim().toLowerCase().replace(/\s+/g, '');
    if (['', 'white', '#fff', '#ffffff', 'rgb(255,255,255)', 'transparent', 'none', 'inherit', 'initial', 'auto', 'window', 'rgba(0,0,0,0)'].includes(v)) {
        return null;
    }
    return toHex(v) || null;
}

const NAMED_COLORS = {
    red: '#FF0000', green: '#008000', blue: '#0000FF', yellow: '#FFFF00', lime: '#00FF00', aqua: '#00FFFF',
    cyan: '#00FFFF', fuchsia: '#FF00FF', magenta: '#FF00FF', gray: '#808080', grey: '#808080', silver: '#C0C0C0',
    maroon: '#800000', navy: '#000080', olive: '#808000', purple: '#800080', teal: '#008080', orange: '#FFA500',
    darkblue: '#00008B', darkred: '#8B0000', darkgreen: '#006400', darkcyan: '#008B8B', darkmagenta: '#8B008B',
    darkyellow: '#808000', darkgray: '#A9A9A9', lightgray: '#D3D3D3',
};

function toHex(v) {
    if (NAMED_COLORS[v]) {
        return NAMED_COLORS[v];
    }
    let match = v.match(/^#([0-9a-f]{3})$/);
    if (match) {
        return '#' + match[1].split('').map(c => c + c).join('').toUpperCase();
    }
    if (/^#[0-9a-f]{6}$/.test(v)) {
        return v.toUpperCase();
    }
    match = v.match(/^rgba?\((\d+),(\d+),(\d+)(?:,([\d.]+))?\)$/);
    if (match) {
        if (match[4] !== undefined && parseFloat(match[4]) === 0) {
            return null;
        }
        return '#' + match.slice(1, 4).map(n => Math.min(255, parseInt(n, 10)).toString(16).padStart(2, '0')).join('').toUpperCase();
    }
    return null;
}
